import { getRouteApi } from '@tanstack/react-router'
import type { DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import { toast } from '@/hooks/useToast'

import CurriculumForm from '../components/CurriculumForm'
import CurriculumLayoutHeader from '../components/CurriculumLayoutHeader'
import type { CurriculumFormStep } from '../hooks/useCurriculumForm'
import useEditCurriculum from '../mutations/useEditCurriculum'

const routeApi = getRouteApi('/_auth/curricula/$curriculumId/edit')

const CurriculumManagementEditView = () => {
  const { t } = useTranslation('curriculumsManagement')

  const navigate = routeApi.useNavigate()
  const curriculum = routeApi.useLoaderData()

  const defaultValues: DefaultValues<CurriculumFormStep> = {
    name: curriculum.name,
    educationLevel: curriculum.educationLevel.id,
    aim: curriculum.aim,
    baskets: curriculum.baskets.map(basket => ({
      type: basket.basketType,
      courses: basket.basketCourses.map(course => ({
        course: course.course?.id ?? '',
        timeUnit: course.timeUnit,
        semesters: course.semesters.map(semester => ({
          hours: semester.hours,
          isActive: semester.isActive
        }))
      }))
    }))
  }

  const { mutate: editCurriculum, isPending } = useEditCurriculum({
    id: curriculum.id,
    onSuccess: ({ variables, response }) => {
      toast({
        variant: 'success',
        title: t('toast.update-success', { NAME: variables.name })
      })

      navigate({
        to: '/curricula/$curriculumId',
        params: { curriculumId: response.id }
      })
    },
    onError: ({ variables }) => {
      toast({
        variant: 'error',
        title: t('toast.update-error', { NAME: variables.name })
      })
    }
  })

  const { mutate: saveAsDraft, isPending: isSavingAsDraft } = useEditCurriculum(
    {
      id: curriculum.id,
      saveAsDraft: true,
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t('toast.save-as-draft-success')
        })
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.save-as-draft-error')
        })
      }
    }
  )

  const handleSubmit = (formData: CurriculumFormStep) => {
    if (!('baskets' in formData)) return

    editCurriculum(formData)
  }

  const handleSaveAsDraft = (formData: CurriculumFormStep) => {
    if (!('baskets' in formData)) return

    saveAsDraft(formData)
  }

  return (
    <BasicLayout
      header={
        <CurriculumLayoutHeader
          title={curriculum.name}
          status={curriculum.status}
        />
      }
      subheader={t('header.edit-curriculum')}
      breadcrumbs={[
        {
          title: t('header.curricula'),
          to: '/curricula'
        },
        {
          title: t('header.edit-curriculum')
        }
      ]}
      moduleName={t('header.administration', { ns: 'common' })}
    >
      <CurriculumForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onSaveAsDraft={handleSaveAsDraft}
        isLoading={isPending || isSavingAsDraft}
        curriculumId={curriculum.id}
      />
    </BasicLayout>
  )
}

export default CurriculumManagementEditView
